/* De broodkruimel boven elke CRM-pagina, afgeleid van de route. Stond vast op
 * "CRM Dashboard > Overzicht"; nu zie je waar je bent en klik je een niveau terug.
 * Bij een project staat nummer en naam erbij (uit de korte projectlijst, die ook
 * de projectwisselaar gebruikt), bij een locatie, organisatie of contact de naam (uit
 * wat die pagina al las). */

import { Fragment } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { useProjectenKort } from '@/features/projectlijst/hooks';
import { siteSleutel } from '@/features/sites/data/sleutels';
import { haalSite } from '@/features/sites/data/leesSite';
import { crmClient } from '@/features/ems/crm/data/crmClient';
import { contactSleutel, organisatieSleutel } from '@/features/relaties/data/sleutels';
import { haalContact, haalOrganisatie } from '@/features/relaties/data/pagina';

const LABELS: Record<string, string> = {
  intelligence: 'Intelligence', klanten: 'Organisaties', organisaties: 'Organisaties', contacts: 'Contacten', contacten: 'Contacten',
  leads: 'Leads', projects: 'Projecten', projecten: 'Projecten', 'project-templates': 'Projecttemplates', sites: 'Locaties',
  todos: "To-do's", intakes: 'Intakes', intake: 'Intake', tickets: 'Tickets', notifications: 'Meldingen', agenda: 'Agenda',
  calendar: 'Agenda', 'battery-planner': 'Batterijen', batterijen: 'Batterijen', businesscases: 'Businesscases',
  businesscase: 'Businesscase', tekenaar: 'Tekenaar', flexe: 'Flex-E simulatie', machines: 'Machines', scenarios: "Scenario's",
  analyses: 'Analyses', piekvermogen: 'Piekvermogen', transcripties: 'Transcripties', epse: 'EPSE', hub: 'Hub', 'hub-v2': 'Hub v2',
  laadplein: 'Laadplein', subsidies: 'Subsidies', charging: 'Charging', acquisitie: 'Acquisitie', realisatie: 'Realisatie',
  operatie: 'Operatie', archief: 'Archief', export: 'Export', concurrentie: 'Concurrentie', wensen: 'Wensen',
  opportunities: 'Opportunities', presentatie: 'Presentatie', 'gauw-tabel': 'GAUW-tabel', locatie: 'Kavel', users: 'Gebruikers',
  gebruikers: 'Gebruikers', 'portal-users': 'Portaalgebruikers', 'user-management': 'Gebruikersbeheer', gebruikersbeheer: 'Gebruikersbeheer',
  'error-reports': 'Foutmeldingen', documentatie: 'Documentatie', sandbox: 'Sandbox', 'energy-flow': 'Energy flow', website: 'Website',
  taken: 'Taken', content: 'Content', notities: 'Notities', ideeen: 'Ideeën', settings: 'Instellingen', instellingen: 'Instellingen',
  quotes: 'Offertes', mail: 'Mail-triage', 'quote-templates': 'Offertesjablonen', design: 'Ontwerp', ems: 'EMS', new: 'Nieuw',
  report: 'Rapport', viewer: 'Briefing',
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface Kruimel {
  label: string;
  naar: string;
}

/** De kruimels na "CRM" voor een pad onder /crm; puur, voor de toets. */
export function kruimelsVoor(
  pad: string,
  projectNaam: (id: string) => string | null,
  siteNaam: (id: string) => string | null = () => null,
  relatieNaam: (id: string) => string | null = () => null,
  caseNaam: (id: string) => string | null = () => null,
): Kruimel[] {
  const delen = pad.replace(/^\/crm\/?/, '').split('/').filter(Boolean);
  const uit: Kruimel[] = [];
  delen.forEach((deel, i) => {
    const naar = `/crm/${delen.slice(0, i + 1).join('/')}`;
    const vorige = delen[i - 1];
    let label = LABELS[deel];
    if (!label && (vorige === 'projects' || vorige === 'projecten')) label = projectNaam(deel) ?? 'Project';
    if (!label && vorige === 'sites' && UUID.test(deel)) label = siteNaam(deel) ?? 'Locatie';
    if (!label && (vorige === 'organisaties' || vorige === 'klanten') && UUID.test(deel)) label = relatieNaam(deel) ?? 'Organisatie';
    if (!label && (vorige === 'contacts' || vorige === 'contacten') && UUID.test(deel)) label = relatieNaam(deel) ?? 'Contact';
    if (!label && vorige === 'businesscases' && UUID.test(deel)) label = caseNaam(deel) ?? 'Businesscase';
    if (!label) label = UUID.test(deel) || /^\d+$/.test(deel) ? 'Detail' : decodeURIComponent(deel);
    uit.push({ label, naar });
  });
  return uit;
}

export function CrmKruimels() {
  const { pathname } = useLocation();
  const opProject = /^\/crm\/project(en|s)\/[^/]+/.test(pathname);
  const kort = useProjectenKort({ enabled: opProject });
  const naam = (id: string) => {
    const p = kort.data?.find((x) => x.id === id);
    return p ? [p.project_number, p.name].filter(Boolean).join(' ') : null;
  };
  /* De sitepagina heeft de site al gelezen; hier alleen meelezen uit die cache, nooit zelf ophalen
     (enabled: false). Dezelfde queryFn als de pagina, zodat een verversing altijd de echte vraag doet. */
  const siteId = /^\/crm\/sites\/([^/]+)/.exec(pathname)?.[1];
  const site = useQuery({
    queryKey: siteSleutel(siteId),
    queryFn: () => haalSite(crmClient, siteId as string),
    enabled: false,
  });
  const siteNaam = (id: string) => (id === siteId && site.data ? [site.data.nummer, site.data.naam].filter(Boolean).join(' ') : null);
  /* idem voor de organisatie- en contactpagina */
  const orgId = /^\/crm\/(?:organisaties|klanten)\/([^/]+)/.exec(pathname)?.[1];
  const contactId = /^\/crm\/(?:contacts|contacten)\/([^/]+)/.exec(pathname)?.[1];
  const org = useQuery({
    queryKey: organisatieSleutel(orgId),
    queryFn: () => haalOrganisatie(crmClient, orgId as string),
    enabled: false,
  });
  const contact = useQuery({
    queryKey: contactSleutel(contactId),
    queryFn: () => haalContact(crmClient, contactId as string),
    enabled: false,
  });
  const relatieNaam = (id: string) =>
    (id === orgId && org.data?.organisatie.naam) || (id === contactId && contact.data?.contact.naam) || null;
  /* de businesscasepagina houdt haar gegevens niet in React Query: hier alleen de naam */
  const caseId = /^\/crm\/businesscases\/([0-9a-f-]{36})/i.exec(pathname)?.[1];
  const bc = useQuery({
    queryKey: ['businesscases', 'naam', caseId],
    enabled: Boolean(caseId),
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await (crmClient as any).from('businesscases').select('name').eq('id', caseId).maybeSingle();
      return (data?.name as string | undefined) ?? null;
    },
  });
  const caseNaam = (id: string) => (id === caseId ? bc.data ?? null : null);
  const kruimels = kruimelsVoor(pathname, naam, siteNaam, relatieNaam, caseNaam);

  return (
    <Breadcrumb className="min-w-0">
      <BreadcrumbList className="flex-nowrap">
        <BreadcrumbItem className={kruimels.length ? 'hidden md:block' : undefined}>
          {kruimels.length ? (
            <BreadcrumbLink asChild>
              <NavLink to="/crm" end>CRM</NavLink>
            </BreadcrumbLink>
          ) : (
            <BreadcrumbPage>Dashboard</BreadcrumbPage>
          )}
        </BreadcrumbItem>
        {kruimels.map((k, i) => {
          const laatste = i === kruimels.length - 1;
          return (
            <Fragment key={k.naar}>
              <BreadcrumbSeparator className={i === 0 ? 'hidden md:block' : undefined} />
              <BreadcrumbItem className={laatste ? 'min-w-0' : i < kruimels.length - 2 ? 'hidden lg:block' : 'shrink-0'}>
                {laatste ? (
                  <BreadcrumbPage className="block max-w-[40vw] truncate">{k.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <NavLink to={k.naar} end>{k.label}</NavLink>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
